import React from 'react';
import { SubscriptionPlan, PricingTier } from '../../types/subscription';

export type PlanTheme = 'default' | 'emerald' | 'purple';

interface PlanCardProps {
  tier: PricingTier;
  displayPrice: number;
  monthlyPrice: number;
  currencySymbol: string;
  billingCycle: 'monthly' | 'annual';
  isCurrentPlan: boolean;
  isLoading: boolean;
  promoApplied: boolean;
  savings: number;
  onSubscribe: (planId: SubscriptionPlan) => void;
  getButtonText: (planId: SubscriptionPlan) => string;
  theme?: PlanTheme;
  showTrialBadge?: boolean;
}

const themeColors = {
  default: {
    border: 'border-gray-200',
    borderHighlight: 'ring-2 ring-blue-600',
    badge: 'bg-blue-600 text-white',
    badgeBasic: 'bg-blue-500 text-white',
    check: 'text-green-500',
    button: 'bg-gray-900 text-white hover:bg-gray-800',
    buttonHighlight: 'bg-blue-600 text-white hover:bg-blue-700',
    planName: 'text-gray-900',
    trialBadge: 'bg-blue-100 text-blue-700',
  },
  emerald: {
    border: 'border-emerald-300',
    borderHighlight: 'border-emerald-600 ring-2 ring-emerald-600',
    badge: 'bg-emerald-600 text-white',
    badgeBasic: 'bg-emerald-500 text-white',
    check: 'text-emerald-500',
    button: 'bg-emerald-600 text-white hover:bg-emerald-700',
    buttonHighlight: 'bg-emerald-600 text-white hover:bg-emerald-700',
    planName: 'text-emerald-700',
    trialBadge: 'bg-emerald-100 text-emerald-700',
  },
  purple: {
    border: 'border-purple-300',
    borderHighlight: 'border-purple-600 ring-2 ring-purple-600',
    badge: 'bg-purple-600 text-white',
    badgeBasic: 'bg-purple-500 text-white',
    check: 'text-purple-500',
    button: 'bg-purple-600 text-white hover:bg-purple-700',
    buttonHighlight: 'bg-purple-600 text-white hover:bg-purple-700',
    planName: 'text-purple-700',
    trialBadge: 'bg-purple-100 text-purple-700',
  },
};

const PlanCard: React.FC<PlanCardProps> = ({
  tier,
  displayPrice,
  monthlyPrice,
  currencySymbol,
  billingCycle,
  isCurrentPlan,
  isLoading,
  promoApplied,
  savings,
  onSubscribe,
  getButtonText,
  theme = 'default',
  showTrialBadge = false,
}) => {
  const colors = themeColors[theme];
  const isPro = tier.id.includes('pro') || tier.id.includes('interview_pro');

  return (
    <div
      className={`relative flex flex-col p-8 bg-white rounded-2xl shadow-lg ${
        tier.highlighted || isPro
          ? colors.borderHighlight + ' shadow-xl'
          : `border-2 ${colors.border}`
      }`}
    >
      {/* Theme-specific badges */}
      {theme !== 'default' && (
        <div className={`absolute -top-4 left-6 px-4 py-1 rounded-full text-sm font-semibold ${
          isPro ? colors.badge : (colors.badgeBasic || colors.badge)
        }`}>
          {tier.name.includes('Pro') ? `${tier.name.split(' ')[0]} Pro` : `${tier.name.split(' ')[0]} Basic`}
        </div>
      )}

      {/* Most Popular Badge */}
      {(tier.highlighted || isPro) && (
        <div className={`absolute -top-4 ${theme === 'default' ? 'left-1/2 -translate-x-1/2' : 'right-6'}`}>
          <span className={`inline-flex items-center py-1 rounded-full font-semibold whitespace-nowrap ${
            theme === 'default' ? 'px-4 text-sm bg-blue-600 text-white' : 'px-3 text-xs bg-yellow-400 text-yellow-900'
          }`}>
            Most Popular
          </span>
        </div>
      )}

      {/* Discount Badge */}
      {savings > 0 && tier.id !== 'free' && !isPro && (
        <div className="absolute -top-3 -right-3">
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-orange-500 text-white">
            Save {savings}% with code
          </span>
        </div>
      )}

      {/* Plan Name */}
      <h3 className={`text-xl font-semibold ${theme === 'default' ? colors.planName : `mt-2 ${colors.planName}`}`}>
        {tier.name}
      </h3>
      <p className="mt-2 text-sm text-gray-500">{tier.description}</p>

      {/* Price */}
      <div className="mt-6">
        <div className="flex items-baseline">
          <span className="text-4xl font-extrabold text-gray-900">
            {currencySymbol}
            {monthlyPrice.toFixed(2)}
          </span>
          <span className="ml-1 text-gray-500">/month</span>
        </div>
        {billingCycle === 'annual' && displayPrice > 0 && (
          <p className="mt-1 text-sm text-gray-500">
            {currencySymbol}{displayPrice.toFixed(2)} billed annually
          </p>
        )}
        {promoApplied && displayPrice > 0 && (
          <p className="mt-1 text-sm text-green-600 font-medium">
            🎉 Early Bird price!
          </p>
        )}
      </div>

      {/* Trial Badge - only show for paid plans */}
      {showTrialBadge && tier.id !== 'free' && (
        <div className={`mt-4 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${colors.trialBadge}`}>
          3-day free trial
        </div>
      )}

      {/* Features */}
      <ul className={`${showTrialBadge ? 'mt-6' : 'mt-8'} space-y-4 flex-grow`}>
        {tier.features.map((feature, index) => (
          <li key={index} className="flex items-start">
            <svg
              className={`flex-shrink-0 w-5 h-5 ${colors.check}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
            <span className="ml-3 text-sm text-gray-600">{feature}</span>
          </li>
        ))}
      </ul>

      {/* CTA Button */}
      <button
        onClick={() => onSubscribe(tier.id)}
        disabled={isCurrentPlan || isLoading}
        className={`mt-8 w-full py-3 px-4 rounded-lg font-medium transition-colors ${
          isCurrentPlan
            ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
            : tier.highlighted || isPro || theme !== 'default'
            ? colors.buttonHighlight
            : colors.button
        } ${isLoading ? 'opacity-50 cursor-wait' : ''}`}
      >
        {isLoading ? 'Loading...' : getButtonText(tier.id)}
      </button>
    </div>
  );
};

export default PlanCard;
